import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const SOPs = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">SOPs</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Retrieve and manage SOPs. SOPs are generated from projects and represent structured procedures
        with actionable steps. Use these endpoints to list, retrieve, and work with your SOPs.
      </p>

      <EndpointCard
        method="GET"
        path="/projects/{project_uuid}/sops"
        title="List SOPs in Project"
        description="Retrieves all SOPs associated with a project. Can optionally filter by status (e.g., PROPOSED, PUBLISHED)."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            project_uuid: { required: true, description: 'The unique identifier of the project' }
          },
          query: {
            status: { required: false, description: 'Filter by SOP status (PROPOSED, PUBLISHED)' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>status</code></td>
                  <td>string</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>Filter by SOP status (PROPOSED, PUBLISHED)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/projects/{project_uuid}/sops?status=PROPOSED" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`[
  {
    "uuid": "{sop_uuid}",
    "project_uuid": "{project_uuid}",
    "title": "Customer Support Escalation SOP",
    "status": "PROPOSED",
    "version": 1,
    "scope": {
      "goal": "Provide clear steps for handling customer support escalations.",
      "summary": "This SOP outlines the process for escalating customer issues."
    }
  }
]`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/sops/{sop_uuid}"
        title="Get SOP"
        description="Retrieves a single SOP with all its steps and metadata. Users can only access SOPs from projects they own or are members of."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            sop_uuid: { required: true, description: 'The unique identifier of the SOP' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/sops/{sop_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "project_uuid": "{project_uuid}",
  "title": "Customer Support Escalation SOP",
  "status": "PROPOSED",
  "version": 1,
  "scope": {
    "goal": "Provide clear steps for handling customer support escalations.",
    "summary": "This SOP outlines the process for escalating customer issues."
  },
  "content": {
    "introduction": "...",
    "conclusion": "..."
  },
  "steps": [
    {
      "uuid": "{step_uuid}",
      "sop_uuid": "{sop_uuid}",
      "step_id": "step_1",
      "action_text": "Identify escalation trigger",
      "order_index": 0,
      "step_metadata": {
        "title": "Identify Escalation Trigger",
        "description": "Determine if the customer's issue meets escalation criteria."
      }
    }
  ]
}`} />
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default SOPs;
